import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CramSheetInteractionSchema,
  FlashcardInteractionSchema,
  NoteInteractionSchema,
  QuestionInteractionSchema,
  SubjectSubTopicReduxSchema,
  SubTopicReduxSchema,
  VideoFinishCountSchmea,
  VideoWatchCountSchmea,
  VideoWatchTimeSchema,
} from "@tsTypes/redux-schema";
import { RootState } from "../../app/store";
import {
  fillMissingSubtopicUserReduxMetadata,
  isoStringToUnixDay,
} from "../../lib/redux/helpers";

const initialState: SubjectSubTopicReduxSchema = {};

export const SubtopicUserReduxMetadataSlice = createSlice({
  name: "SubtopicUserReduxMetadata",
  initialState,
  reducers: {
    setSubTopicState: (
      state,
      action: PayloadAction<
        | {
            [subjectDocumentId: string]:
              | {
                  [topicDocumentId: number]:
                    | {
                        [subTopicDocumentId: string]: SubTopicReduxSchema;
                      }
                    | undefined;
                }
              | undefined;
          }
        | undefined
      >
    ) => {
      return { ...action.payload };
    },
    videoView: (state, action: PayloadAction<VideoWatchCountSchmea>) => {
      const {
        videoDocumentId,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentIds,
        date,
        duration,
        number,
      } = action.payload;
      subTopicDocumentIds.forEach((subTopicDocumentId) => {
        fillMissingSubtopicUserReduxMetadata(
          state,
          subjectDocumentId,
          topicDocumentId,
          subTopicDocumentId,
          "videos",
          videoDocumentId
        );
        const existingMeta =
          state[subjectDocumentId]![topicDocumentId]![subTopicDocumentId]!
            .videos![videoDocumentId];
        // Overwrite draft meta above with correct data. Also handles case where video already existed and just needed updating
        state[subjectDocumentId]![topicDocumentId]![
          subTopicDocumentId
        ]!.videos![videoDocumentId] = {
          num: number,
          dur: duration,
          wt: existingMeta.wt,
          df: existingMeta.df,
          dv: [...existingMeta.dv, isoStringToUnixDay(date)],
        };
      });
    },
    videoFinish: (state, action: PayloadAction<VideoFinishCountSchmea>) => {
      const {
        videoDocumentId,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentIds,
        date,
      } = action.payload;
      subTopicDocumentIds.forEach((subTopicDocumentId) => {
        fillMissingSubtopicUserReduxMetadata(
          state,
          subjectDocumentId,
          topicDocumentId,
          subTopicDocumentId,
          "videos",
          videoDocumentId
        );
        // Overwrite draft meta above with correct data. Also handles case where video already existed and just needed updating
        const existingMeta =
          state[subjectDocumentId]![topicDocumentId]![subTopicDocumentId]!
            .videos![videoDocumentId];
        state[subjectDocumentId]![topicDocumentId]![
          subTopicDocumentId
        ]!.videos![videoDocumentId] = {
          num: existingMeta.num,
          dur: existingMeta.dur,
          wt: existingMeta.wt,
          df: [...existingMeta.df, isoStringToUnixDay(date)],
          dv: existingMeta.dv,
        };
      });
    },
    videoWatchTime: (state, action: PayloadAction<VideoWatchTimeSchema>) => {
      const {
        videoDocumentId,
        watchTime,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentIds,
      } = action.payload;
      subTopicDocumentIds.forEach((subTopicDocumentId) => {
        fillMissingSubtopicUserReduxMetadata(
          state,
          subjectDocumentId,
          topicDocumentId,
          subTopicDocumentId,
          "videos",
          videoDocumentId
        );
        // update relevant attributes
        state[subjectDocumentId]![topicDocumentId]![
          subTopicDocumentId
        ]!.videos![videoDocumentId].wt += watchTime;
      });
    },
    noteView: (state, action: PayloadAction<NoteInteractionSchema>) => {
      const {
        noteSectionId,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentId,
        date,
      } = action.payload;
      fillMissingSubtopicUserReduxMetadata(
        state,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentId,
        "notes",
        noteSectionId.toString()
      );
      // Overwrite draft meta above with correct data. Also handles case where note already existed and just needed updating
      state[subjectDocumentId]![topicDocumentId]![subTopicDocumentId]!.notes![
        noteSectionId
      ].dr.push(isoStringToUnixDay(date));
    },
    flashcardInteraction: (
      state,
      action: PayloadAction<FlashcardInteractionSchema>
    ) => {
      const {
        subTopicDocumentId,
        date,
        cardDocumentId,
        isStarAction,
        subjectDocumentId,
        topicDocumentId,
      } = action.payload;
      fillMissingSubtopicUserReduxMetadata(
        state,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentId,
        "flashcards",
        cardDocumentId
      );
      // Overwrite draft meta above with correct data. Also handles case where card already existed and just needed updating
      isStarAction
        ? state[subjectDocumentId]![topicDocumentId]![
            subTopicDocumentId
          ]!.flashcards![cardDocumentId].ds.push(isoStringToUnixDay(date))
        : state[subjectDocumentId]![topicDocumentId]![
            subTopicDocumentId
          ]!.flashcards![cardDocumentId].dv.push(isoStringToUnixDay(date));
    },
    questionAttempt: (
      state,
      action: PayloadAction<QuestionInteractionSchema>
    ) => {
      const {
        questionDocumentId,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentId,
        date,
        isMultipleChoice,
        percentagePerformance,
      } = action.payload;
      const questionTypeKey = isMultipleChoice ? "mcQuestions" : "laQuestions";
      fillMissingSubtopicUserReduxMetadata(
        state,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentId,
        questionTypeKey,
        questionDocumentId
      );
      // Overwrite draft meta above with correct data. Also handles case where question already existed and just needed updating
      state[subjectDocumentId]![topicDocumentId]![
        subTopicDocumentId
      ]!.questions![questionDocumentId].da.push(isoStringToUnixDay(date));
      state[subjectDocumentId]![topicDocumentId]![
        subTopicDocumentId
      ]!.questions![questionDocumentId].perf.push(percentagePerformance);
      state[subjectDocumentId]![topicDocumentId]![
        subTopicDocumentId
      ]!.questions![questionDocumentId].ismc = isMultipleChoice;
    },
    cramsheetDownload: (
      state,
      action: PayloadAction<CramSheetInteractionSchema>
    ) => {
      const {
        sheetDocumentId,
        subjectDocumentId,
        topicDocumentId,
        subTopicDocumentIds,
        date,
        number,
      } = action.payload;
      subTopicDocumentIds.forEach((subTopicDocumentId) => {
        fillMissingSubtopicUserReduxMetadata(
          state,
          subjectDocumentId,
          topicDocumentId,
          subTopicDocumentId,
          "cramSheets",
          sheetDocumentId
        );
        state[subjectDocumentId]![topicDocumentId]![
          subTopicDocumentId
        ]!.cramSheets![sheetDocumentId].num = number;
        state[subjectDocumentId]![topicDocumentId]![
          subTopicDocumentId
        ]!.cramSheets![sheetDocumentId].dd.push(isoStringToUnixDay(date));
      });
    },
  },
});

export const {
  videoView,
  videoFinish,
  videoWatchTime,
  noteView,
  flashcardInteraction,
  questionAttempt,
  cramsheetDownload,
  setSubTopicState,
} = SubtopicUserReduxMetadataSlice.actions;

export function selectVideoById(
  state: RootState,
  subjectDocumentId: string,
  topicDocumentId: string,
  subTopicDocumentId: string,
  videoDocumentId: string
) {
  return state.SubtopicUserReduxMetadata?.[subjectDocumentId]?.[
    topicDocumentId
  ]?.[subTopicDocumentId]?.videos?.[videoDocumentId];
}
export function selectNoteById(
  state: RootState,
  subjectDocumentId: string,
  topicDocumentId: string,
  subTopicDocumentId: string,
  noteSectionId: string
) {
  return state.SubtopicUserReduxMetadata?.[subjectDocumentId]?.[
    topicDocumentId
  ]?.[subTopicDocumentId]?.notes?.[noteSectionId];
}
export function selectFlashcardById(
  state: RootState,
  subjectDocumentId: string,
  topicDocumentId: string,
  subTopicDocumentId: string,
  cardDocumentId: string
) {
  return state.SubtopicUserReduxMetadata?.[subjectDocumentId]?.[
    topicDocumentId
  ]?.[subTopicDocumentId]?.flashcards?.[cardDocumentId];
}
export function selectQuestionById(
  state: RootState,
  subjectDocumentId: string,
  topicDocumentId: string,
  subTopicDocumentId: string,
  questionDocumentId: string
) {
  return state.SubtopicUserReduxMetadata?.[subjectDocumentId]?.[
    topicDocumentId
  ]?.[subTopicDocumentId]?.questions?.[questionDocumentId];
}
export function selectSubTopicCramsheets(
  state: RootState,
  subjectDocumentId: string,
  topicDocumentId: string,
  subTopicDocumentId: string
) {
  return state.SubtopicUserReduxMetadata?.[subjectDocumentId]?.[
    topicDocumentId
  ]?.[subTopicDocumentId]?.cramSheets;
}

export default SubtopicUserReduxMetadataSlice.reducer;
